import { clsx } from 'clsx';
import { type ComponentProps, type ReactNode, type Ref, useEffect, useId, useImperativeHandle, useRef } from 'react';

export type CheckboxProps = Omit<ComponentProps<'input'>, 'type' | 'ref'> & {
  ref?: Ref<HTMLInputElement | null>;
  /** Shows the "some selected" dash (e.g. a select-all header). `checked` should be false meanwhile. */
  indeterminate?: boolean;
  /** Visible label; when omitted pass `aria-label`. */
  label?: ReactNode;
  description?: ReactNode;
};

export function Checkbox({ ref, indeterminate = false, label, description, className, id, ...rest }: CheckboxProps) {
  const inner = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => inner.current as HTMLInputElement);
  useEffect(() => {
    if (inner.current) inner.current.indeterminate = indeterminate;
  }, [indeterminate]);
  const generated = useId();
  const inputId = id ?? generated;

  const box = (
    <input
      ref={inner}
      id={inputId}
      type="checkbox"
      aria-checked={indeterminate ? 'mixed' : undefined}
      className={clsx(
        'size-4 shrink-0 cursor-pointer rounded border-slate-300 accent-ember-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ember-500 disabled:cursor-not-allowed disabled:opacity-50',
        !label && className,
      )}
      {...rest}
    />
  );
  if (!label) return box;
  return (
    <div className={clsx('flex items-start gap-2', className)}>
      <span className="flex h-5 items-center">{box}</span>
      <label htmlFor={inputId} className="cursor-pointer text-[13px] leading-5 text-slate-700 select-none">
        {label}
        {description && <span className="block text-xs text-slate-500">{description}</span>}
      </label>
    </div>
  );
}

export type SwitchProps = {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  size?: 'sm' | 'md';
  id?: string;
  className?: string;
  'aria-label'?: string;
};

/** On/off toggle (`role="switch"`). Use for settings that apply immediately. */
export function Switch({
  checked,
  onChange,
  label,
  description,
  disabled,
  size = 'md',
  id,
  className,
  'aria-label': ariaLabel,
}: SwitchProps) {
  const generated = useId();
  const switchId = id ?? generated;
  const button = (
    <button
      id={switchId}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label ? undefined : ariaLabel}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={clsx(
        'relative inline-flex shrink-0 cursor-pointer items-center rounded-full transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ember-500 disabled:cursor-not-allowed disabled:opacity-50',
        size === 'sm' ? 'h-4 w-7' : 'h-5 w-9',
        checked ? 'bg-ember-500' : 'bg-slate-300',
      )}
    >
      <span
        aria-hidden
        className={clsx(
          'inline-block rounded-full bg-white shadow-sm transition-transform',
          size === 'sm' ? 'size-3' : 'size-4',
          checked ? (size === 'sm' ? 'translate-x-3.5' : 'translate-x-4.5') : 'translate-x-0.5',
        )}
      />
    </button>
  );
  if (!label) return <span className={className}>{button}</span>;
  return (
    <div className={clsx('flex items-start justify-between gap-4', className)}>
      <label htmlFor={switchId} className="cursor-pointer text-[13px] text-slate-700 select-none">
        <span className="font-medium">{label}</span>
        {description && <span className="mt-0.5 block text-xs text-slate-500">{description}</span>}
      </label>
      {button}
    </div>
  );
}
